import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/effects/use-debounce';
import { odooApiService } from '@/services/odoo.service';
import type { OdooResourceType, OdooSearchItem } from '@/types/odoo.types';

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 300;

interface UseOdooSearchOptions {
  /** The Odoo entity type to search. */
  resourceType: OdooResourceType;
  /** Whether the search is enabled. */
  enabled?: boolean;
}

/**
 * Hook that provides debounced search against the Odoo API.
 * Only triggers a request when the search term has >= 2 characters.
 */
export function useOdooSearch({ resourceType, enabled = true }: UseOdooSearchOptions) {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedTerm = useDebounce(searchTerm, DEBOUNCE_MS);

  const shouldSearch = enabled && debouncedTerm.length >= MIN_SEARCH_LENGTH;

  const { data, isLoading, isFetching, error } = useQuery<OdooSearchItem[]>({
    queryKey: ['odoo-search', resourceType, debouncedTerm],
    queryFn: () => odooApiService.searchOdoo(resourceType, debouncedTerm),
    enabled: shouldSearch,
    staleTime: 30 * 1000, // 30 seconds
    keepPreviousData: true,
  });

  return {
    /** Current search term. */
    searchTerm,
    /** Update the search input value. */
    setSearchTerm,
    /** Debounced search results. */
    results: data ?? [],
    /** True while the initial request is loading. */
    isLoading: shouldSearch && isLoading,
    /** True while any fetch is in progress (including background refetches). */
    isFetching: shouldSearch && isFetching,
    /** Error from the request, if any. */
    error,
  };
}
